"use client";
import React, { useCallback, useMemo, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from 'react-redux';
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button, Col, Form, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { saveProductMapping } from "../../store/slices/saveProductMapping";
import { getConfig } from '../../store/slices/getConfig';

//  Updated initial field
const initialField = { pimlocale: "", shopifylocale: "" };


const validateMappings = (values) => {
  const errors = {};

  const mappings = values.mappings || [];

  // No rows → valid
  if (mappings.length === 0) {
    return errors;
  }

  // Exactly one row
  if (mappings.length === 1) {
    const { pimlocale, shopifylocale } = mappings[0];

    // BOTH EMPTY → VALID
    if (!pimlocale && !shopifylocale) {
      return errors;
    }

    // One is filled, so both are required
    if (!pimlocale || !shopifylocale) {
      errors.mappings = [{}];

      if (!pimlocale) {
        errors.mappings[0].pimlocale =
          "pimlocale is required";
      }

      if (!shopifylocale) {
        errors.mappings[0].shopifylocale =
          "shopifylocale is required";
      }

      return errors;
    }

    return errors;
  }

  // Multiple rows
  const mappingErrors = [];

  mappings.forEach((mapping, index) => {
    const rowErrors = {};

    if (!mapping.pimlocale) {
      rowErrors.pimlocale = "pimlocale is required";
    }

    if (!mapping.shopifylocale) {
      rowErrors.shopifylocale = "shopifylocale is required";
    }

    mappingErrors[index] = rowErrors;
  });

  // Only add mappings errors if there is actually an error
  if (
    mappingErrors.some(
      (row) => Object.keys(row).length > 0
    )
  ) {
    errors.mappings = mappingErrors;
  }

  return errors;
};



function marketLocalePopup({ toggle, setToggle, showToast, pimLocale, shopifyLocale, reference }) {
  const dispatch = useDispatch();
  const storeDomain = useSelector(state => state.config?.clientStoreHash);
  const formik = useFormik({
    initialValues: {
      mappings: [initialField],
    },
    validate: validateMappings,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
        localStorage.setItem(`${storeDomain+'|'+reference}`, JSON.stringify(values));
        const savedData = JSON.parse(localStorage.getItem(`${storeDomain+'|'+reference}`));
        setToggle(false);
    },
  });

  useEffect(() => {
    if (!reference) return;

    const savedData = localStorage.getItem(`${storeDomain+'|'+reference}`);

     if (!savedData || !Array.isArray(JSON.parse(savedData)?.mappings) ||  JSON.parse(savedData)?.mappings?.length === 0) {
      formik.setFieldValue("mappings", [initialField]);
      return;
    }

    if (!savedData) return;

    try {
      const parsedData = JSON.parse(savedData);

      if (Array.isArray(parsedData?.mappings)) {
        formik.setFieldValue(
          "mappings",
          parsedData.mappings
        );
      }
    } catch (error) {
      console.error("Failed to parse localStorage data:", error);
    }
  }, [reference]);

  const { values, errors, touched, handleChange, handleSubmit } = formik;
  
  const handleAddFields = useCallback(() => {
    const updatedMappings = [...values?.mappings, initialField];
    formik.setFieldValue("mappings", updatedMappings);
  }, [values?.mappings, formik]);

  const handleRemoveFields = useCallback(
    (index) => {
      const updatedMappings = values?.mappings?.filter((_, i) => i !== index);
      formik.setFieldValue("mappings", updatedMappings);
    },
    [values?.mappings, formik]
  );

  // Memoize the last index
  const lastIndex = useMemo(
    () => values?.mappings?.length - 1,
    [values?.mappings?.length]
  );

const filteringOptions = useCallback(
  (options = [], currentIndex, key) => {
    if (!Array.isArray(options)) return [];

    const selectedValues = formik?.values?.mappings
      .map((item, index) => {
        if (index === currentIndex) return null;
        return item?.[key];
      })
      .filter(Boolean);

    return options.filter(
      (option) => !selectedValues.includes(option.code)
    );
  },
  [formik?.values?.mappings]
);
  

return (
    <>
      <Modal
        show={toggle != null}
        onHide={() => setToggle(null)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="custom-modal-width"
        backdrop="static"
        centered
      >
        <div className="bg-white p-6 rounded-lg shadow-lg p-5 rounded-3">
          {/* <h5 className="fw-bold mb-4 border-bottom border-gray">
            Edit Secondary shopifylocales
          </h5> */}

          <Row className="mappingInput">
            <Col sm={5}>
              <p className="mb-4">
                <strong>PIM Locale</strong>
              </p>
            </Col>
            <Col sm={5} className="ms-30 pimAttribute">
              <p className="mb-4">
                <strong>Shopify Locale</strong>
              </p>
            </Col>
          </Row>

          {values.mappings.map((inputField, index) => (
            <React.Fragment key={index}>
              <Row className="mb-3">
                {/* pimlocale Dropdown */}
                <Col sm={5}>
                  <Form.Group controlId={`mappings.${index}.pimlocale`}>
                    <Form.Select
                      name={`mappings[${index}].pimlocale`}
                      value={inputField.pimlocale}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.pimlocale &&
                        touched.mappings?.[index]?.pimlocale
                      }
                    >
                      <option value="">Select the PIM Locale</option>
                      {filteringOptions(pimLocale,index,"pimlocale")?.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.pimlocale}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Compare arrows icon */}
                <Col
                  sm={1}
                  className="d-flex justify-content-center compareIcon"
                >
                  <CompareArrowsIcon className="mt-2" />
                </Col>

                {/* shopifylocale Dropdown */}
                <Col sm={5}>
                  <Form.Group controlId={`mappings.${index}.shopifylocale`}>
                    <Form.Select
                      name={`mappings[${index}].shopifylocale`}
                      value={inputField.shopifylocale}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.shopifylocale &&
                        touched.mappings?.[index]?.shopifylocale
                      }
                    >
                    <option value="">Select the Shopify Locale</option>
                    {filteringOptions(shopifyLocale,index,"shopifylocale")?.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.shopifylocale}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Remove button */}
                <Col sm={1} className="ps-0 pt-1">
                  {values.mappings.length > 1 && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleRemoveFields(index)}
                      className="channelActionBtn p-0"
                    >
                      <DeleteOutlineIcon style={{ fontSize: "30px"}}/>
                    </Button>
                  )}
                </Col>
              </Row>

              {/* Add button only on last row */}
              {index === lastIndex && (
                <Row>
                  <Col
                    sm={10}
                    className="d-flex justify-content-center mt-3 ms-4"
                  >
                    <Button
                      type="button"
                      onClick={handleAddFields}
                      style={{
                        border: "1px solid lightgray",
                        borderRadius: "7px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                      }}
                    >
                      Add new mapping
                    </Button>
                  </Col>
                </Row>
              )}
            </React.Fragment>
          ))}
        </div>

        <Modal.Footer>
          <button
            onClick={() => setToggle(false)}
            className="modal_cancel_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Cancel
          </button>
          <button
          type="button"
            onClick={handleSubmit}
            className="modal_save_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default marketLocalePopup;
